import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Fab,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
} from '@material-ui/core';
import {
  Close,
  Send,
  MenuBook,
  Chat as ChatIcon,
  Delete,
  ExpandMore,
  Storage,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import {
  sendChatMessage,
  sendChatMessageStreaming,
  getLLMStatus,
  getLLMRateLimitStatus
} from 'app/main/settings/store/actions/llmProviders.actions';

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1200,
    backgroundColor: '#0078d4', // Microsoft Copilot blue
    color: '#fff',
    '&:hover': {
      backgroundColor: '#106ebe',
    },
    // AI Rainbow halo
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -5,
      left: -5,
      right: -5,
      bottom: -5,
      borderRadius: '50%',
      background: 'linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #7b68ee, #ff0080)',
      backgroundSize: '400% 400%',
      animation: '$rainbowHalo 15s ease infinite',
      zIndex: -1,
      opacity: 0.8,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      borderRadius: '50%',
      backgroundColor: '#0078d4',
      zIndex: -1,
    },
  },
  '@keyframes rainbowHalo': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    },
  },
  fabIcon: {
    width: 28,
    height: 28,
  },
  drawer: {
    width: 400,
    maxWidth: '100vw',
  },
  drawerPaper: {
    width: 400,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1e1e2d', // Dark background
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: '#151521', // Darker header
    color: '#fff',
    borderBottom: '1px solid #2d2d3a',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#1e1e2d', // Dark messages area
  },
  message: {
    marginBottom: theme.spacing(2),
    maxWidth: '85%',
  },
  userMessage: {
    marginLeft: 'auto',
    backgroundColor: '#0078d4',
    color: '#fff',
    borderRadius: '18px 18px 4px 18px',
    padding: theme.spacing(1.5, 2),
  },
  assistantMessage: {
    backgroundColor: '#2d2d3a', // Dark message bubble
    color: '#e0e0e0',
    borderRadius: '18px 18px 18px 4px',
    padding: theme.spacing(1.5, 2),
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  documentBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
    backgroundColor: 'rgba(0, 120, 212, 0.2)', // Dark badge
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    color: '#5ba7e8',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    padding: theme.spacing(2),
    borderTop: '1px solid #2d2d3a',
    backgroundColor: '#151521', // Dark input area
  },
  textField: {
    flex: 1,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2d2d3a',
      color: '#e0e0e0',
      '& fieldset': {
        borderColor: '#3d3d4a',
      },
      '&:hover fieldset': {
        borderColor: '#4d4d5a',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#0078d4',
      },
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#8a8a9a',
      opacity: 1,
    },
  },
  sendButton: {
    color: '#0078d4',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#8a8a9a',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  emptyIcon: {
    fontSize: 64,
    opacity: 0.5,
    marginBottom: theme.spacing(2),
    color: '#5d5d6a',
  },
  rateLimitInfo: {
    padding: theme.spacing(1, 2),
    backgroundColor: 'rgba(230, 81, 0, 0.15)', // Dark warning
    fontSize: '0.75rem',
    color: '#ff9800',
    textAlign: 'center',
  },
  loadingMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#2d2d3a',
    color: '#e0e0e0',
    borderRadius: '18px 18px 18px 4px',
    maxWidth: '85%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  // AI Thinking Animation
  aiThinkingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  aiThinkingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    animation: '$aiPulse 1.4s ease-in-out infinite',
    '&:nth-child(1)': {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      animationDelay: '0s',
    },
    '&:nth-child(2)': {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      animationDelay: '0.2s',
    },
    '&:nth-child(3)': {
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      animationDelay: '0.4s',
    },
    '&:nth-child(4)': {
      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      animationDelay: '0.6s',
    },
  },
  '@keyframes aiPulse': {
    '0%, 80%, 100%': {
      transform: 'scale(0.6)',
      opacity: 0.5,
    },
    '40%': {
      transform: 'scale(1.2)',
      opacity: 1,
    },
  },
  streamingCursor: {
    display: 'inline-block',
    width: 2,
    height: '1em',
    backgroundColor: '#0078d4',
    marginLeft: 2,
    animation: '$blink 1s step-end infinite',
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  timestamp: {
    fontSize: '0.7rem',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
    color: '#8a8a9a',
  },
  clearButton: {
    color: '#fff',
    opacity: 0.8,
    '&:hover': {
      opacity: 1,
    },
  },
  // Markdown styles
  markdownContent: {
    '& ul, & ol': {
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(2.5),
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    },
    '& code': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      padding: '2px 6px',
      borderRadius: 4,
      fontFamily: 'monospace',
      fontSize: '0.85em',
    },
    '& pre': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      padding: theme.spacing(1),
      borderRadius: 4,
      overflow: 'auto',
      margin: theme.spacing(0.5, 0),
    },
    '& p': {
      margin: theme.spacing(0.5, 0),
    },
    '& strong': {
      fontWeight: 600,
    },
  },
}));

/**
 * Simple Markdown renderer for chat messages
 * Handles: **bold**, `code`, - lists, numbered lists
 */
function renderMarkdown(text) {
  if (!text) return null;

  // Split into lines and process
  var lines = text.split('\n');
  var elements = [];
  var listItems = [];
  var listType = null; // 'ul' or 'ol'
  var key = 0;

  function flushList() {
    if (listItems.length > 0) {
      if (listType === 'ol') {
        elements.push(React.createElement('ol', { key: 'list-' + key++ }, listItems));
      } else {
        elements.push(React.createElement('ul', { key: 'list-' + key++ }, listItems));
      }
      listItems = [];
      listType = null;
    }
  }

  function processInline(str) {
    // Process **bold** and `code`
    var parts = [];
    var regex = /(\*\*(.+?)\*\*|`(.+?)`)/g;
    var lastIndex = 0;
    var match;

    while ((match = regex.exec(str)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(str.slice(lastIndex, match.index));
      }
      // Add formatted element
      if (match[2]) {
        parts.push(React.createElement('strong', { key: 'b-' + match.index }, match[2]));
      } else if (match[3]) {
        parts.push(React.createElement('code', { key: 'c-' + match.index }, match[3]));
      }
      lastIndex = regex.lastIndex;
    }
    // Add remaining text
    if (lastIndex < str.length) {
      parts.push(str.slice(lastIndex));
    }
    return parts.length > 0 ? parts : [str];
  }

  for (var i = 0; i < lines.length; i++) {
    var line = lines[i];
    
    // Check for unordered list (- item or * item)
    var ulMatch = line.match(/^\s*[-*]\s+(.+)$/);
    if (ulMatch) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(React.createElement('li', { key: 'li-' + i }, processInline(ulMatch[1])));
      continue;
    }

    // Check for ordered list (1. item)
    var olMatch = line.match(/^\s*\d+\.\s+(.+)$/);
    if (olMatch) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(React.createElement('li', { key: 'li-' + i }, processInline(olMatch[1])));
      continue;
    }

    // Not a list item - flush any pending list
    flushList();

    // Empty line
    if (line.trim() === '') {
      elements.push(React.createElement('br', { key: 'br-' + i }));
      continue;
    }

    // Regular paragraph
    elements.push(React.createElement('p', { key: 'p-' + i, style: { margin: '4px 0' } }, processInline(line)));
  }

  // Flush any remaining list
  flushList();

  return elements;
}

const CopilotChatWidget = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const abortStreamRef = useRef(null);
  const userScrolledUpRef = useRef(false);

  // Redux state
  const llmConfig = useSelector(({ settings }) => settings.llmProviders?.config);
  const chatLoading = useSelector(({ settings }) => settings.llmProviders?.chatLoading);
  const rateLimit = useSelector(({ settings }) => settings.llmProviders?.rateLimit);

  // Local state (session-only as per spec)
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [streamingMessageId, setStreamingMessageId] = useState(null);

  // Load config on mount
  useEffect(() => {
    dispatch(getLLMStatus());
    dispatch(getLLMRateLimitStatus());
  }, [dispatch]);

  // Scroll to bottom when messages change, but only if user hasn't scrolled up
  useEffect(() => {
    if (messagesEndRef.current && !userScrolledUpRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatLoading]);

  // Reset scroll tracking when streaming ends
  useEffect(() => {
    if (!streamingMessageId) {
      userScrolledUpRef.current = false;
    }
  }, [streamingMessageId]);

  // Handle scroll events to detect user scrolling up
  const handleMessagesScroll = (e) => {
    const container = e.target;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    userScrolledUpRef.current = !isNearBottom;
  };

  // Don't render if LLM is not enabled
  if (!llmConfig?.enabled) {
    return null;
  }

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      dispatch(getLLMRateLimitStatus());
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || chatLoading) return;

    // Cancel any existing stream
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }

    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmedMessage,
      timestamp: new Date(),
    };

    // Create placeholder for streaming assistant message
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      source: null,
      sources: [],
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputValue('');
    setStreamingMessageId(assistantMessageId);

    // Build conversation history for context
    const conversationHistory = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Start streaming
    abortStreamRef.current = dispatch(
      sendChatMessageStreaming(
        trimmedMessage,
        conversationHistory,
        // onToken - append each token to the streaming message
        (token) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? { ...m, content: m.content + token }
                : m
            )
          );
        },
        // onComplete - finalize the message
        (fullResponse, metadata) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? {
                    ...m,
                    content: fullResponse,
                    source: metadata.source,
                    sources: metadata.sources,
                    isStreaming: false,
                  }
                : m
            )
          );
          setStreamingMessageId(null);
          abortStreamRef.current = null;
        },
        // onError - show error message
        (error) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? {
                    ...m,
                    content: 'Sorry, I encountered an error processing your request. Please try again.',
                    isError: true,
                    isStreaming: false,
                  }
                : m
            )
          );
          setStreamingMessageId(null);
          abortStreamRef.current = null;
        }
      )
    );
  };

  const handleClear = () => {
    setMessages([]);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Action Button */}
      <Tooltip title="Heidi AI Assistant" placement="left">
        <Fab
          className={classes.fab}
          onClick={handleToggle}
          aria-label="Open Heidi AI Assistant"
        >
          <ChatIcon className={classes.fabIcon} />
        </Fab>
      </Tooltip>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <ChatIcon />
            <Typography variant="h6">Heidi AI Assistant</Typography>
          </div>
          <div>
            <Tooltip title="Clear conversation">
              <IconButton
                className={classes.clearButton}
                onClick={handleClear}
                size="small"
              >
                <Delete />
              </IconButton>
            </Tooltip>
            <IconButton
              className={classes.clearButton}
              onClick={handleClose}
              size="small"
            >
              <Close />
            </IconButton>
          </div>
        </div>

        {/* Rate Limit Warning */}
        {rateLimit?.isLimited && (
          <div className={classes.rateLimitInfo}>
            Daily message limit reached. Resets at{' '}
            {new Date(rateLimit.resetsAt).toLocaleTimeString()}
          </div>
        )}

        {/* Messages */}
        <div 
          className={classes.messagesContainer}
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
        >
          {messages.length === 0 ? (
            <div className={classes.emptyState}>
              <ChatIcon className={classes.emptyIcon} />
              <Typography variant="h6" gutterBottom>
                How can I help you?
              </Typography>
              <Typography variant="body2">
                Ask me anything about Heidi - cluster management, storage configuration,
                deployments, and more.
              </Typography>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className={classes.message}>
                  <Paper
                    className={
                      message.role === 'user'
                        ? classes.userMessage
                        : classes.assistantMessage
                    }
                    elevation={0}
                  >
                    {message.role === 'user' ? (
                      <Typography
                        variant="body2"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {message.content}
                      </Typography>
                    ) : (
                      <div className={classes.markdownContent}>
                        {message.isStreaming && !message.content ? (
                          // Show AI thinking animation while waiting for first token
                          <div className={classes.aiThinkingContainer}>
                            <div className={classes.aiThinkingDot} />
                            <div className={classes.aiThinkingDot} />
                            <div className={classes.aiThinkingDot} />
                            <div className={classes.aiThinkingDot} />
                          </div>
                        ) : (
                          <>
                            {renderMarkdown(message.content)}
                            {message.isStreaming && (
                              <span className={classes.streamingCursor} />
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {message.source === 'documentation' && (
                      <div className={classes.documentBadge}>
                        <MenuBook fontSize="inherit" />
                        <span>From documentation</span>
                      </div>
                    )}
                    {message.source === 'database' && (
                      <div className={classes.documentBadge} style={{ backgroundColor: '#1a3a1a' }}>
                        <Storage fontSize="inherit" />
                        <span>Live data</span>
                      </div>
                    )}
                    {message.source === 'database+documentation' && (
                      <div className={classes.documentBadge} style={{ backgroundColor: '#1a2a3a' }}>
                        <Storage fontSize="inherit" />
                        <MenuBook fontSize="inherit" style={{ marginLeft: 4 }} />
                        <span>Live data + docs</span>
                      </div>
                    )}
                  </Paper>
                  <Typography
                    className={classes.timestamp}
                    align={message.role === 'user' ? 'right' : 'left'}
                  >
                    {formatTime(message.timestamp)}
                  </Typography>
                </div>
              ))}
              {chatLoading && !streamingMessageId && (
                <div className={classes.loadingMessage}>
                  <div className={classes.aiThinkingContainer}>
                    <div className={classes.aiThinkingDot} />
                    <div className={classes.aiThinkingDot} />
                    <div className={classes.aiThinkingDot} />
                    <div className={classes.aiThinkingDot} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className={classes.inputContainer}>
          <TextField
            className={classes.textField}
            placeholder="Type your message..."
            multiline
            maxRows={4}
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={chatLoading || streamingMessageId || rateLimit?.isLimited}
            variant="outlined"
            size="small"
          />
          <IconButton
            className={classes.sendButton}
            onClick={handleSend}
            disabled={!inputValue.trim() || chatLoading || streamingMessageId || rateLimit?.isLimited}
          >
            <Send />
          </IconButton>
        </div>

        {/* Rate Limit Info */}
        {rateLimit?.messagesLimit > 0 && !rateLimit?.isLimited && (
          <Typography
            variant="caption"
            align="center"
            style={{ padding: '4px', color: '#8a8a9a', backgroundColor: '#151521' }}
          >
            {rateLimit.messagesUsed} / {rateLimit.messagesLimit} messages today
          </Typography>
        )}
      </Drawer>
    </>
  );
};

export default CopilotChatWidget;
